import type { UploadPolicy } from '../shared/models/UploadPolicy'
import type {
  UploadWorkspaceFileChunkRequest,
  WorkspaceFileUploadResponse,
  WorkspaceItem,
  WorkspaceUploadProgress,
} from '../shared/models/WorkspaceItem'
import { resolveUploadFormat, getChunkSize } from './fileWorksapceUploadUtil'


export async function uploadWorkspaceFiles({
  files,
  policy,
  workspaceId,
  parentId,
  uploadChunk,
  onProgress,
  onUploaded,
  uploadIds,
  allowDuplicate
}: {
  files: File[]
  policy: UploadPolicy
  workspaceId: string
  parentId: string | null
  uploadChunk: (request: UploadWorkspaceFileChunkRequest) => Promise<WorkspaceFileUploadResponse>
  onProgress: (progress: WorkspaceUploadProgress) => void
  onUploaded?: (item: WorkspaceItem) => void
  uploadIds?: Map<File, string>
  allowDuplicate?: boolean
}): Promise<WorkspaceItem[]> {
  if (files.length === 0) return []

  
  const chunkSize = getChunkSize(policy)
  const totalBytes = files.reduce((total, file) => total + file.size, 0)
  let uploadedBytes = 0
  let fileIndex = 0
  const completed: WorkspaceItem[] = []
  const report = (status: WorkspaceUploadProgress['status'], error?: string) => {
    onProgress({
      fileName: files[fileIndex].name,
      fileIndex,
      totalFiles: files.length,
      uploadedBytes,
      totalBytes,
      status,
      error,
    })
  }

  try {
    const mediaFiles = files.map((file) => {
      const format = resolveUploadFormat(file.name, file.type, policy)
       
      if (!format || file.size > policy.maxFileSizeBytes) {
        throw new Error(
            `Only supported files up to ${policy.maxFileSizeBytes / (1024 * 1024)} MB are allowed. No files were uploaded.`
        )
      }
      return { file, mediaType: format.mediaType }
    })
    for (fileIndex = 0; fileIndex < files.length; fileIndex += 1) {
      const { file, mediaType } = mediaFiles[fileIndex]
      const expectedChunkCount = Math.max(1, Math.ceil(file.size / chunkSize))
      const uploadId = uploadIds?.get(file) ?? crypto.randomUUID()
      uploadIds?.set(file, uploadId)
      report('uploading')

      for (let chunkNumber = 0; chunkNumber < expectedChunkCount; chunkNumber += 1) {
        const start = chunkNumber * chunkSize
        const chunk = file.slice(start, Math.min(start + chunkSize, file.size))
        const isFinalChunk = chunkNumber === expectedChunkCount - 1
        const result = await uploadChunk({
          workspaceId,
          uploadId,
          chunkNumber,
          expectedChunkCount,
          expectedSizeBytes: file.size,
          fileName: file.name,
          mediaType,
          parentId,
          chunk,
          isFinalChunk,
          allowDuplicate
        })
        uploadedBytes += chunk.size
        report('uploading')
        if (isFinalChunk) {
          if (!('id' in result) || result.type !== 'file') {
            throw new Error('The server has not confirmed the completed upload. Please retry.')
          }
          completed.push(result)
          onUploaded?.(result)
        }
      }
    }
    fileIndex = files.length - 1
    report('complete')
    return completed
  } catch (error) {
    report('error', error instanceof Error ? error.message : 'File upload failed.')
    throw error
  }
}
